class VariantRadios extends HTMLElement {
  constructor() {
    super();

    this.updateOptions();
    this.updateMasterId();
    this.addListeners();
  }

  /**
   * Adds event listeners.
   */
  addListeners() {
    this.addEventListener("change", this.onVariantChange.bind(this));

    subscribe(PUB_SUB_EVENTS.productPriceChange, additionalPrice => this.updateButtonPrice(additionalPrice));
  }

  /**
   * Handles variant change events.
   * @param {Event} event
   */
  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true);
    this.removeErrorMessage();
    this.toggleAddButton(!this.currentVariant);

    if (this.currentVariant) {
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo(event);
    }

    if (!this.currentVariant) this.setUnavailable();
  }

  /**
   * Sets the options based on selected inputs.
   */
  updateOptions() {
    this.options = Array.from(this.variantFieldsets).map(fieldset => {
      const input = fieldset.querySelector("[data-input]:checked");

      return input.value;
    });
  }

  /**
   * Updates the current variant based on selected options.
   */
  updateMasterId() {
    this.currentVariant = this.variantData.find(variant => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false);
    });
  }

  /**
   * Updates the URL with the current variant ID.
   */
  updateURL() {
    if (!this.currentVariant) return;

    const url = `${this.productUrl}?variant=${this.currentVariant.id}`;

    window.history.replaceState({}, "", url);
  }

  /**
   * Updates the hidden variant input.
   */
  updateVariantInput() {
    this.variantInput.value = this.currentVariant.id;
    this.stickyVariantInput.value = this.currentVariant.id;
    this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Sets the state when no variant is found.
   */
  removeErrorMessage() {
    const section = this.closest("[data-product]");

    if (!section) return;

    const productForm = section.querySelector("[data-product-form]");

    if (productForm) productForm.handleErrorMessage();
  }

  /**
   * Sets the state when no variant is found.
   * @param {Event} event
   */
  renderProductInfo(event) {
    const sectionId = this.sectionId;
    const productUrl = this.productUrl;
    const requestedVariantId = this.currentVariant.id;
    const newProductUrl = event.target.dataset.productUrl;
    const url = `${newProductUrl}?variant=${requestedVariantId}&section_id=${sectionId}`;

    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        if (productUrl !== newProductUrl) return this.updateSectionContent(html, true);
        if (this.currentVariant.id !== requestedVariantId) return;

        this.updateSectionContent(html);
        this.updateButton(html);
      });
  }

  /**
   * Sets the state when no variant is found.
   */
  updateSectionContent(html, variants = false) {
    this.updateContent(html, "price", true);
    this.updateContent(html, "availability");
    this.updateContent(html, "sku");
    this.updateContent(html, "featured-media");
    this.updateContent(html, "additions", true);
    this.updateContent(html, "option", true);

    if (variants) this.updateContent(html, "variants");
  }

  /**
   * Updates specific content based on selector.
   * @param {Document} html
   * @param {string} selector
   * @param {boolean} outer
   */
  updateContent(html, selector, outer = false) {
    const oldElements = document.querySelectorAll(`[data-variant-${selector}]`);
    const newElements = html.querySelectorAll(`[data-variant-${selector}]`);
    const method = outer ? "outerHTML" : "innerHTML";

    oldElements.forEach((oldElement, index) => {
      const newElement = newElements[index];

      if (newElement) oldElement[method] = newElement[method];
    });
  }

  /**
   * Updates the add to cart button state.
   * @param {Document} html
   */
  updateButton(html) {
    const addButtonUpdated = html.querySelector("[data-add-to-cart]");
    const isDisabled = addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true;
    const buttonText = isDisabled ? "outOfStock" : "addToCart";

    this.toggleAddButton(isDisabled, buttonText);
  }

  /**
   * Updates the add to cart button price.
   * @param {number} additionalPrice
   */
  updateButtonPrice(additionalPrice) {
    if (!this.currentVariant || !this.addToCartButtonText) return;

    const basePrice = this.currentVariant.price;
    const totalPrice = basePrice + additionalPrice;

    this.addToCartButtonPrice.textContent = formatMoney(totalPrice);
  }

  /**
   * Sets the state when no variant is found.
   * @param {boolean} disable
   * @param {string} buttonText
   */
  toggleAddButton(disable = true, buttonText = "addToCart") {
    if (!this.addToCartButton) return;

    this.addToCartButton.toggleAttribute("disabled", disable);
    this.addToCartButtonText.textContent = window.variantStrings[buttonText];
  }

  /**
   * Updates content when a variant is unavailable.
   */
  setUnavailable() {
    this.toggleAddButton(true, "unavailable");

    publish(PUB_SUB_EVENTS.hideAdditions);
  }

  /**
   * Returns variant data from JSON script.
   * @returns {Array}
   */
  get variantData() {
    const variantDataScript = this.querySelector('[type="application/json"]');
    return JSON.parse(variantDataScript.textContent);
  }

  /**
   * Returns all variant fieldsets.
   * @returns {NodeListOf<Element>}
   */
  get variantFieldsets() {
    return this.querySelectorAll("[data-variant-fieldset]");
  }

  /**
   * Returns the hidden variant input.
   * @returns {HTMLInputElement}
   */
  get variantInput() {
    return document.querySelector("[data-variant-id]");
  }

  /**
   * Returns the hidden variant input.
   * @returns {HTMLInputElement}
   */
  get stickyVariantInput() {
    return document.querySelector("[data-sticky-add-to-cart] [data-variant-id]");
  }

  /**
   * Returns the product URL.
   * @returns {string}
   */
  get productUrl() {
    return this.dataset.productUrl;
  }

  /**
   * Returns the section ID.
   * @returns {string}
   */
  get sectionId() {
    return this.dataset.section;
  }

  /**
   * Returns the add to cart button.
   * @returns {HTMLButtonElement}
   */
  get addToCartButton() {
    return document.querySelector("[data-add-to-cart]");
  }

  /**
   * Returns the add to cart button text.
   * @returns {HTMLElement}
   */
  get addToCartButtonText() {
    return this.addToCartButton?.querySelector("[data-add-to-cart-text]");
  }

  get addToCartButtonPrice() {
    return this.addToCartButton?.querySelector("[data-variant-price]");
  }
}

if (!customElements.get("variant-radios")) {
  customElements.define("variant-radios", VariantRadios);
}